import React, { FC, useCallback } from 'react';

import { Center, Stack } from '@chakra-ui/react';
import { Application } from 'pixi.js';
import { MdCenterFocusWeak } from 'react-icons/all';

import { PrimaryIconButton } from 'components/DataEntry';
import { EnvironmentIcon, ZoomInIcon, ZoomOutIcon } from 'components/Icons';
import { AGraphEdge } from 'components/Visualization/PixiGraph/core/Edge';
import { AGraph } from 'components/Visualization/PixiGraph/core/Graph';
import { AGraphNode } from 'components/Visualization/PixiGraph/core/Node';

interface IGraphScale {
  app: Application;
  graph: AGraph<AGraphNode, AGraphEdge>;

  toggleVulnerability?();

  showVulnerability?: boolean;
}

export const GraphScale: FC<IGraphScale> = props => {
  const { app, graph, showVulnerability, toggleVulnerability } = props;

  const handleZoomOut = useCallback(() => {
    const scale = Math.max(app.stage.scale.y - 0.1, 0.2);
    app.stage.scale.set(scale);
  }, [app.stage.scale]);

  const handleZoomIn = useCallback(() => {
    const scale = Math.min(app.stage.scale.y + 0.1, 1.2);
    app.stage.scale.set(scale);
  }, [app.stage.scale]);

  const handleFocusGraph = useCallback(() => {
    const graphWithAdditionalNodes = graph as typeof graph & {
      headers?: AGraphNode[];
      pagination?: AGraphNode[];
    };
    const visibleNodes = [
      ...graph.nodes,
      ...(graphWithAdditionalNodes.headers ?? []),
      ...(graphWithAdditionalNodes.pagination ?? []),
    ].filter(node => node.visible);
    if (!visibleNodes.length) return;

    const minX = Math.min(...visibleNodes.map(node => node.x));
    const maxX = Math.max(...visibleNodes.map(node => node.x + node.w));
    const minY = Math.min(...visibleNodes.map(node => node.y));
    const maxY = Math.max(...visibleNodes.map(node => node.y + node.h));
    const scale = app.stage.scale.x;

    app.stage.x = (app.screen.width - (maxX - minX) * scale) / 2 - minX * scale;
    app.stage.y =
      (app.screen.height - (maxY - minY) * scale) / 2 - minY * scale;
  }, [app.screen.height, app.screen.width, app.stage, graph]);

  return (
    <Stack pos={'absolute'} bottom={4} left={4} justify={'center'}>
      <Center mb={4}>
        <PrimaryIconButton
          icon={<EnvironmentIcon />}
          borderRadius={'50%'}
          aria-label={'toggle-vulnerabilities'}
          tooltip={
            showVulnerability ? 'Hide Vulnerabilities' : 'Show Vulnerabilities'
          }
          onClick={() => toggleVulnerability?.()}
          size={'sm'}
          bg={showVulnerability ? 'primary' : 'white'}
          color={showVulnerability ? 'white' : 'primary'}
        />
      </Center>

      <Center>
        <PrimaryIconButton
          icon={<MdCenterFocusWeak />}
          borderRadius={'50%'}
          aria-label={'center-graph'}
          tooltip="Center Graph"
          onClick={handleFocusGraph}
          size={'sm'}
        />
      </Center>
      <Center>
        <PrimaryIconButton
          icon={<ZoomOutIcon />}
          borderRadius={'50%'}
          aria-label={'zoom-out'}
          tooltip="Zoom Out"
          onClick={handleZoomOut}
          size={'sm'}
        />
      </Center>
      <Center>
        <PrimaryIconButton
          icon={<ZoomInIcon />}
          borderRadius={'50%'}
          aria-label={'zoom-in'}
          tooltip="Zoom In"
          onClick={handleZoomIn}
          size={'md'}
        />
      </Center>
    </Stack>
  );
};
